import type { LucideIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { useFormContext } from "react-hook-form";

import { DatePickerWithRange } from "@/components/ui/date-picker-with-range";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/components/ui/form";

type Props = {
  name: string;
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  description?: string;
  placeholder: string;
  format?: string;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onChange?: (value: DateRange | null) => void;
  Icon?: LucideIcon;
  highlightWhenSelected?: boolean;
};

export const DatePickerWithRangeInput = ({
  label,
  name,
  className,
  disabled = false,
  placeholder,
  description,
  onChange,
  Icon,
  highlightWhenSelected = true,
}: Props) => {
  const form = useFormContext();
  const { error } = useFormField();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const handleOnChange = (value: DateRange | null) => {
          onChange?.(value);
          field.onChange(value);
        };

        return (
          <FormItem className="w-fit">
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <DatePickerWithRange
                value={field.value}
                disabled={disabled}
                className={className}
                onChange={handleOnChange}
                placeholder={placeholder}
                aria-invalid={error ? true : undefined}
                Icon={Icon}
                highlightWhenSelected={highlightWhenSelected}
              />
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
